import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Text,
  ScrollView,
  Platform,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { Button, List, Divider } from 'react-native-paper';
import { Floor } from '../types';
import { colors } from '../constants/theme';

interface FloorDropdownProps {
  selectedFloor?: string;
  onFloorSelect: (floor: string | undefined) => void;
  style?: StyleProp<ViewStyle>;
  label?: string;
}

// Floor options in display order (bottom to top)
const FLOOR_OPTIONS: { value: Floor; label: string }[] = [
  { value: 'Exterior', label: 'Exterior' },
  { value: 'Basement', label: 'Basement' },
  { value: '1', label: 'Floor 1' },
  { value: '2', label: 'Floor 2' },
  { value: '3', label: 'Floor 3' },
  { value: '4', label: 'Floor 4' },
  { value: '5', label: 'Floor 5' },
  { value: '6', label: 'Floor 6' },
  { value: 'Attic', label: 'Attic' },
];

export const FloorDropdown: React.FC<FloorDropdownProps> = ({
  selectedFloor,
  onFloorSelect,
  style,
  label = "Floor",
}) => {
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectedFloorLabel, setSelectedFloorLabel] = useState<string>('');

  useEffect(() => {
    if (selectedFloor) {
      const option = FLOOR_OPTIONS.find(opt => opt.value === selectedFloor);
      setSelectedFloorLabel(option?.label || selectedFloor);
    } else {
      setSelectedFloorLabel('');
    }
  }, [selectedFloor]);

  const handleFloorSelect = (floor: Floor | undefined) => {
    onFloorSelect(floor);
    setShowSelectionModal(false);
  };

  const handleClearSelection = () => {
    onFloorSelect(undefined);
    setShowSelectionModal(false);
  };

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.label}>{label}</Text>

      <TouchableOpacity
        style={styles.button}
        onPress={() => setShowSelectionModal(true)}
      >
        <Text style={styles.buttonText}>
          {selectedFloorLabel || `Select ${label}`}
        </Text>
      </TouchableOpacity>

      <Modal
        visible={showSelectionModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowSelectionModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select {label}</Text>
              <Button onPress={() => setShowSelectionModal(false)}>
                Close
              </Button>
            </View>

            <Divider />

            <ScrollView style={styles.listContainer}>
              {/* Clear Selection Option */}
              <List.Item
                title="Clear Selection"
                onPress={handleClearSelection}
                left={props => <List.Icon {...props} icon="close-circle-outline" />}
                style={styles.listItem}
              />

              <Divider />

              {/* Floor Options */}
              {FLOOR_OPTIONS.map((option) => (
                <View key={option.value}>
                  <List.Item
                    title={option.label}
                    onPress={() => handleFloorSelect(option.value)}
                    left={props => <List.Icon {...props} icon={
                      selectedFloor === option.value ? 'check-circle' : 'circle-outline'
                    } />}
                    style={[
                      styles.listItem,
                      selectedFloor === option.value && styles.selectedItem,
                    ]}
                  />
                  <Divider />
                </View>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
    color: colors.text,
  },
  button: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
  },
  buttonText: {
    fontSize: 16,
    color: colors.text,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: colors.background,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '80%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 5,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text,
  },
  listContainer: {
    maxHeight: 500,
  },
  listItem: {
    paddingVertical: 12,
  },
  selectedItem: {
    backgroundColor: colors.primary + '20', // 20% opacity
  },
});
